import { AnyAction } from '@reduxjs/toolkit';
import { combineEpics, StateObservable } from 'redux-observable';
import {
  concat,
  concatMap,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  mapTo,
  Observable,
  of,
  switchMap
} from 'rxjs';
import { SmoMoon } from '../../../../Models/Smo/SmoMoon';
import { MoonListRequestInput } from '../../../MoonListManager/Models/MoonListRequestInput';
import { SmoMoonListManager } from '../../../MoonListManager/SmoMoonListManager';
import ComponentStateActions from '../../Global/ComponentState/ComponentStateActions';
import { StoreDependencies } from '../../Global/StoreDependencies';
import { StoreState } from '../../Global/StoreState';
import UserStoreActions from '../../Global/User/UserStoreActions';
import CreateMoonListActions from './CreateMoonListActions';
import { CreateMoonListState } from './CreateMoonListState';

const componentStatePrefix: string = 'CREATE_MOON_LIST';

function setSearch(
  $action: Observable<AnyAction>,
  $state: StateObservable<StoreState>,
  dependencies: Partial<StoreDependencies>
): Observable<AnyAction> {
  return $action.pipe(
    filter(CreateMoonListActions.setSearch.match),
    map(({ payload }) => payload),
    filter((search) => search.length > 0),
    debounceTime(150),
    distinctUntilChanged(),
    switchMap((search) =>
      dependencies.moonManager!.searchMoonWithName(search, $state.value.createMoonList.searchLanguage)
    ),
    map(CreateMoonListActions.setSearchResults)
  );
}

function selectMoonWithId(
  $action: Observable<AnyAction>,
  $state: StateObservable<StoreState>,
  _dependencies: Partial<StoreDependencies>
): Observable<AnyAction> {
  return $action.pipe(
    filter(CreateMoonListActions.selectMoonWithId.match),
    map(({ payload }) => payload),
    map((moonId) => $state.value.createMoonList.searchResults.find((moon: SmoMoon) => moon.id === moonId)),
    filter((moon) => !!moon),
    concatMap((moon) => [CreateMoonListActions.addMoon(moon), CreateMoonListActions.setSearch('')])
  );
}

function createMoonList(
  $action: Observable<AnyAction>,
  $state: StateObservable<StoreState>,
  dependencies: Partial<StoreDependencies>
): Observable<AnyAction> {
  return $action.pipe(
    filter(CreateMoonListActions.createMoonList.match),
    map(() => $state.value.createMoonList),
    map((moonList: CreateMoonListState) => ({
      name: moonList.name,
      isPublic: moonList.isPublic,
      moonIds: moonList.moons.map((m: SmoMoon) => m.id)
    })),
    switchMap((moonList: MoonListRequestInput) =>
      concat(
        of(ComponentStateActions.setLoading(componentStatePrefix)),
        postMoonList(moonList, dependencies.moonListManager!),
        of(CreateMoonListActions.resetState()),
        of(UserStoreActions.getCurrentUserMoonLists())
      )
    )
  );
}

function postMoonList(moonList: MoonListRequestInput, moonListManager: SmoMoonListManager): Observable<AnyAction> {
  return moonListManager
    .createMoonListWithId(moonList)
    .pipe(mapTo(ComponentStateActions.setSuccess(componentStatePrefix)));
}

export default combineEpics(setSearch, selectMoonWithId, createMoonList);
